import { useEffect, useRef, useState, type ReactNode } from "react";
import { NvButton as Button } from "@/components/navigator/nv-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import type { ChatMessage } from "@/services/chat-service";
import { ChatInput } from "./chat-input";
import { ChatMessageList } from "./chat-message-list";
import {
  AssistantResults,
  type AssistantResultsProps,
} from "./assistant-results";

const SUGGESTIONS = [
  "설비 고장 예측에 시간이 너무 오래 걸려요",
  "계약서 검토 인력이 부족해요",
  "RFP 초안 작성을 자동화하고 싶어요",
];

interface AssistantWorkspaceProps extends AssistantResultsProps {
  sessionId: string;
  activeSessionId?: string;
  draftVersion?: number;
  messages: ChatMessage[];
  isLoading: boolean;
  disabled?: boolean;
  error?: string | null;
  onSend: (message: string) => boolean | void | Promise<boolean>;
  onRetry: () => void;
  canRetry?: boolean;
  inputMaxBytes?: number;
  onNew?: () => void;
  history?: Array<{ id: string; title: string; updated_at?: string }>;
  onDelete?: (id: string) => void;
  historyError?: boolean;
  historyMonths?: number;
  onHistoryRetry?: () => void;
  onRestore?: (id: string) => void;
  toolbar?: ReactNode;
  showResults?: boolean;
  showGuideline?: boolean;
  actions?: ReactNode;
}

export function AssistantWorkspace({
  sessionId,
  activeSessionId,
  draftVersion,
  messages,
  isLoading,
  disabled,
  error,
  onSend,
  onRetry,
  canRetry = true,
  inputMaxBytes = 4096,
  onNew,
  history = [],
  onRestore,
  onDelete,
  historyError,
  historyMonths = 6,
  onHistoryRetry,
  actions,
  toolbar,
  showResults,
  showGuideline = false,
  ...results
}: AssistantWorkspaceProps) {
  const [tab, setTab] = useState("chat");
  const hasResults =
    showResults ?? (!!results.insight || !!results.agents?.length);
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  useEffect(() => {
    const panel = scrollRef.current;
    const last = messages[messages.length - 1];
    if (panel && (atBottom.current || last?.role === "user")) {
      panel.scrollTop = panel.scrollHeight;
    }
  }, [messages, isLoading, tab]);
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-nv-surface font-sans text-nv-text-primary">
      {hasResults && (
        <Tabs
          value={tab}
          onValueChange={setTab}
          className="border-b px-4 py-2 lg:hidden"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="chat">상담 대화</TabsTrigger>
            <TabsTrigger value="results">진단 · 추천</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      <div
        className={cn(
          "grid min-h-0 flex-1 grid-cols-1",
          hasResults
            ? "lg:grid-cols-[400px_minmax(0,1fr)]"
            : "mx-auto w-full max-w-[960px]",
        )}
      >
        <section
          aria-label="AI Assistant 상담"
          className={cn(
            "min-h-0 min-w-0 flex-col gap-4 px-4 py-5 lg:flex",
            hasResults && "border-r border-nv-border",
            !hasResults || tab === "chat" ? "flex" : "hidden",
          )}
        >
          {toolbar}
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={onNew}
              disabled={isLoading || !onNew}
              title={!onNew ? "준비 중인 기능입니다" : undefined}
              size="sm"
              className="bg-nv-surface"
            >
              + 새 상담
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  disabled={isLoading || !onRestore}
                  title={!onRestore ? "준비 중인 기능입니다" : undefined}
                  size="sm"
                  className="bg-nv-surface"
                >
                  지난 상담 ▾
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="max-h-[400px] w-80 overflow-y-auto rounded-nv-card border-nv-border py-3 font-sans shadow-lg"
              >
                {historyError ? (
                  <DropdownMenuItem onSelect={onHistoryRetry}>
                    목록을 불러오지 못했어요 · 다시 시도
                  </DropdownMenuItem>
                ) : history.length ? (
                  history.map((session, index) => {
                    const label = historyGroup(session.updated_at);
                    return (
                      <div key={session.id}>
                        {(index === 0 ||
                          historyGroup(history[index - 1].updated_at) !==
                            label) && (
                          <DropdownMenuLabel className="px-4 pb-1 pt-2 text-[11px] text-nv-text-secondary">
                            {label}
                          </DropdownMenuLabel>
                        )}
                        <div
                          className={cn(
                            "flex items-center",
                            session.id === activeSessionId &&
                              "bg-nv-brand-subtle",
                          )}
                        >
                          <DropdownMenuItem
                            onSelect={() => onRestore?.(session.id)}
                            aria-current={
                              session.id === activeSessionId
                                ? "true"
                                : undefined
                            }
                            className={cn(
                              "min-w-0 flex-1 cursor-pointer gap-2 rounded-none px-4 py-2 text-[13px] focus:bg-nv-brand-subtle",
                              session.id === activeSessionId &&
                                "bg-nv-brand-subtle font-bold",
                            )}
                          >
                            <span
                              className="min-w-0 flex-1 truncate"
                              title={session.title}
                            >
                              {session.title}
                            </span>
                          </DropdownMenuItem>
                          {onDelete && (
                            <DropdownMenuItem
                              aria-label={`${session.title} 삭제`}
                              onSelect={() => onDelete(session.id)}
                              className="shrink-0 cursor-pointer rounded-none px-3 py-2 text-[16px] text-nv-text-secondary focus:bg-nv-brand-subtle"
                            >
                              <span aria-hidden="true">×</span>
                            </DropdownMenuItem>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <DropdownMenuItem disabled>
                    지난 상담이 없습니다
                  </DropdownMenuItem>
                )}
                <p className="px-4 pt-3 text-[11px] text-nv-text-secondary">
                  최근 {historyMonths}개월의 상담을 확인할 수 있어요
                </p>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div
            ref={scrollRef}
            onScroll={(event) => {
              const panel = event.currentTarget;
              atBottom.current =
                panel.scrollHeight - panel.scrollTop - panel.clientHeight < 80;
            }}
            className={cn(
              "custom-scrollbar -mr-3 min-h-0 flex-1 overflow-y-auto pr-3",
              !messages.length && "flex flex-col",
            )}
          >
            {messages.length ? (
              <ChatMessageList messages={messages} isLoading={isLoading} />
            ) : (
              <div className="my-auto flex shrink-0 flex-col items-center justify-center gap-2.5 py-3 text-center sm:py-4">
                <h1 className="text-[22px] font-medium">
                  무엇을 도와드릴까요?
                </h1>
                <p className="text-body-sm leading-relaxed text-content-medium">
                  {hasResults && !results.resultsUnavailable ? (
                    <>
                      해결하고 싶은 업무 문제를 자유롭게 입력하면
                      <br />
                      대화 내용을 바탕으로 맞는 에이전트를 추천합니다.
                    </>
                  ) : (
                    <>
                      해결하고 싶은 업무를 알려 주세요.
                      <br />
                      대화로 요구사항을 정리해 드립니다.
                    </>
                  )}
                </p>
                <div className="flex max-w-full flex-col items-center gap-2 pt-3">
                  {SUGGESTIONS.map((suggestion) => (
                    <Button
                      size="sm"
                      key={suggestion}
                      variant="secondary"
                      disabled={disabled || isLoading}
                      onClick={() => onSend(suggestion)}
                      className="h-auto max-w-full whitespace-normal rounded-nv-chip border-nv-border bg-nv-surface px-3.5 py-2 text-[13px] leading-4"
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {error && (
              <div
                role="alert"
                className="mt-3 flex flex-wrap items-center gap-2 rounded-2xs bg-negative-200 px-3 py-2 text-caption text-content-negative"
              >
                <span>{error}</span>
                {canRetry && (
                  <Button
                    variant="ghost"
                    onClick={onRetry}
                    disabled={isLoading || disabled}
                    className="h-auto p-0 text-caption text-content-negative"
                  >
                    ↻ 다시 시도
                  </Button>
                )}
              </div>
            )}
          </div>
          {actions && (
            <div className="flex shrink-0 flex-wrap justify-end gap-2">
              {actions}
            </div>
          )}
          <ChatInput
            key={sessionId}
            resetVersion={draftVersion}
            maxBytes={inputMaxBytes}
            showAttachment={false}
            showGuideline={showGuideline}
            onSend={onSend}
            disabled={!!disabled || isLoading}
          />
        </section>
        {hasResults && (
          <div
            className={cn(
              "min-h-0 overflow-hidden lg:grid",
              tab === "results" ? "grid" : "hidden",
            )}
          >
            <AssistantResults key={sessionId} {...results} />
          </div>
        )}
      </div>
    </div>
  );
}

function historyGroup(stamp?: string) {
  if (!stamp) return "이전 상담";
  const day = (value: Date) =>
    Date.UTC(value.getFullYear(), value.getMonth(), value.getDate());
  const age = Math.floor((day(new Date()) - day(new Date(stamp))) / 86400000);
  return age <= 0
    ? "오늘"
    : age === 1
      ? "어제"
      : age < 7
        ? "최근 7일"
        : age < 30
          ? "최근 30일"
          : "이전 상담";
}
